import { useState } from 'react';
import { useLang } from '@/hooks/useLang';
import { showreelItems } from '@/mocks/showreel';

const copy = {
  EN: {
    tag: 'Our Work',
    title: 'Selected Projects',
    sub: 'A curated selection of AI-powered productions.',
    viewAll: 'View All Work',
    close: 'Close',
    comingSoon: 'Coming Soon',
  },
  TR: {
    tag: 'Çalışmalarımız',
    title: 'Seçili Projeler',
    sub: 'Yapay zeka destekli prodüksiyonlardan seçmeler.',
    viewAll: 'Tüm Çalışmalar',
    close: 'Kapat',
    comingSoon: 'Yakında',
  },
};

export default function ShowreelSection() {
  const lang = useLang();
  const t = copy[lang];
  const [hovered, setHovered] = useState<number | null>(null);
  const [activeVideo, setActiveVideo] = useState<{ youtubeId: string; title: string } | null>(null);

  const featured = showreelItems[0];
  const grid = showreelItems.slice(1);

  const handleCardClick = (item: typeof showreelItems[0]) => {
    if (item.comingSoon) return;
    if (item.youtubeId) {
      setActiveVideo({ youtubeId: item.youtubeId, title: item.title });
    }
  };

  return (
    <section id="showreel" className="py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <span className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-3 block">
              {t.tag}
            </span>
            <h2
              className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {t.title}
            </h2>
          </div>
          <p className="text-gray-400 text-sm max-w-xs leading-relaxed">{t.sub}</p>
        </div>

        {/* Featured */}
        <div
          className="relative w-full rounded-2xl overflow-hidden mb-6 cursor-pointer group"
          style={{ aspectRatio: '21/9' }}
          onMouseEnter={() => setHovered(0)}
          onMouseLeave={() => setHovered(null)}
          onClick={() => handleCardClick(featured)}
        >
          <img
            src={featured.thumbnail}
            alt={featured.title}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          {featured.youtubeId && (
            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                hovered === 0 ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                <i className="ri-play-fill text-white text-2xl w-6 h-6 flex items-center justify-center ml-1"></i>
              </div>
            </div>
          )}
          <div className="absolute bottom-6 left-8 right-8 flex items-end justify-between">
            <div>
              <span className="text-xs font-semibold text-white/60 tracking-widest uppercase mb-1 block">
                {featured.category}
              </span>
              <h3
                className="text-2xl font-bold text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {featured.title}
              </h3>
            </div>
            <span className="text-white/50 text-sm font-mono">{featured.duration}</span>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {grid.map((item) => (
            <div
              key={item.id}
              className={`relative rounded-xl overflow-hidden group bg-gray-50 ${
                item.comingSoon ? 'cursor-default' : 'cursor-pointer'
              }`}
              style={{ aspectRatio: '16/10' }}
              onMouseEnter={() => setHovered(item.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => handleCardClick(item)}
            >
              <img
                src={item.thumbnail}
                alt={item.title}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

              {/* Coming Soon overlay */}
              {item.comingSoon && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-xs font-bold text-white tracking-widest uppercase">
                    {t.comingSoon}
                  </span>
                </div>
              )}

              {/* Play button */}
              {!item.comingSoon && item.youtubeId && (
                <div
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                    hovered === item.id ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                    <i className="ri-play-fill text-white text-base w-4 h-4 flex items-center justify-center ml-0.5"></i>
                  </div>
                </div>
              )}

              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-xs font-semibold text-white/50 tracking-widest uppercase mb-0.5 block">
                  {item.category}
                </span>
                <h4 className="text-sm font-bold text-white leading-tight">{item.title}</h4>
              </div>
            </div>
          ))}
        </div>

        {/* View All */}
        <div className="text-center">
          <button className="inline-flex items-center gap-2 px-8 py-3 border border-gray-200 text-gray-600 text-sm font-semibold tracking-wide rounded-full hover:border-gray-400 hover:text-gray-900 transition-all duration-300 cursor-pointer whitespace-nowrap">
            {t.viewAll}
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0`}
              title={activeVideo.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
            <button
              onClick={() => setActiveVideo(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
